import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box, Grid, Card, CardContent, Typography, TextField, MenuItem, CircularProgress, Alert,
} from '@mui/material'
import { LocalGasStation, AttachMoney, Speed, LocalAtm, WarningAmber, TrendingUp } from '@mui/icons-material'
import { LineChart } from '@mui/x-charts/LineChart'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { subDays, formatISO } from 'date-fns'
import { PageHeader } from '../../components/PageHeader'
import { StatCard } from '../../components/StatCard'
import { useDevices, Device } from '../../hooks/useDevices'
import { useFuelAnalytics, useFuel } from '../../hooks/useFleet'

function unwrap<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d as T[]
  const o = d as { devices?: T[] } | undefined
  return o?.devices ?? []
}

export default function FuelAnalyticsPage() {
  const { t } = useTranslation()
  const { data: devicesRaw } = useDevices()
  const devices = unwrap<Device>(devicesRaw)

  const [deviceId, setDeviceId] = useState('')
  const [fromDate, setFromDate] = useState<Date>(subDays(new Date(), 30))
  const [toDate, setToDate] = useState<Date>(new Date())

  const from = useMemo(() => formatISO(fromDate), [fromDate])
  const to = useMemo(() => formatISO(toDate), [toDate])

  const { data: analytics, isLoading: analyticsLoading } = useFuelAnalytics(deviceId || undefined, from, to)
  const { data: fuelRecords = [], isLoading: fuelLoading } = useFuel(deviceId || undefined)

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!fuelRecords || fuelRecords.length === 0) return { xAxis: [] as Date[], series: [] as number[] }
    const sorted = [...fuelRecords].sort((a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime())
    return {
      xAxis: sorted.map((r) => new Date(r.recorded_at)),
      series: sorted.map((r) => r.level_l ?? 0),
    }
  }, [fuelRecords])

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <PageHeader
          title={t('fuel.title')}
          subtitle={t('fuel.subtitle')}
        />

        {/* Filters */}
        <Card sx={{ p: 2, mb: 3, border: '1px solid #e2e8f0' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                label={t('fleet.selectDevice')}
                value={deviceId}
                onChange={(e) => { setDeviceId(e.target.value) }}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">{t('fuel.selectDevice')}</MenuItem>
                {devices.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <DateTimePicker
                label={t('playback.from')}
                value={fromDate}
                onChange={(v) => { if (v) { setFromDate(v) } }}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DateTimePicker
                label={t('playback.to')}
                value={toDate}
                onChange={(v) => v && setToDate(v)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
          </Grid>
        </Card>

        {!deviceId ? (
          <Alert severity="info" icon={false} sx={{ mt: 2 }}>
            {t('fuel.selectDevice')}
          </Alert>
        ) : analyticsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Stat cards — 3x2 grid */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title={t('fuel.totalConsumed')}
                  value={`${(analytics?.total_consumed_l ?? 0).toFixed(1)} L`}
                  icon={<LocalGasStation />}
                  color="#34A8E0"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title={t('fuel.totalCost')}
                  value={`$${(analytics?.total_cost ?? 0).toFixed(2)}`}
                  icon={<AttachMoney />}
                  color="#388e3c"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title={t('fuel.costPerKm')}
                  value={`$${(analytics?.cost_per_km ?? 0).toFixed(3)}`}
                  icon={<Speed />}
                  color="#7b1fa2"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title={t('fuel.refillCount')}
                  value={analytics?.refill_count ?? 0}
                  icon={<LocalAtm />}
                  color="#1976d2"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title={t('fuel.theftCount')}
                  value={analytics?.theft_count ?? 0}
                  icon={<WarningAmber />}
                  color="#d32f2f"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title={t('fuel.avgConsumption')}
                  value={`${(analytics?.avg_consumption_per_100km ?? 0).toFixed(1)} L`}
                  icon={<TrendingUp />}
                  color="#f57c00"
                />
              </Grid>
            </Grid>

            {/* Fuel level timeline chart */}
            <Card sx={{ border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('device.fuelRecords')}
                </Typography>
                {fuelLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                  </Box>
                ) : chartData.xAxis.length === 0 ? (
                  <Alert severity="info" icon={false}>
                    {t('device.noFuelRecords')}
                  </Alert>
                ) : (
                  <LineChart
                    height={300}
                    series={[
                      {
                        data: chartData.series,
                        label: t('fuel.totalConsumed'),
                        color: '#34A8E0',
                      },
                    ]}
                    xAxis={[
                      {
                        data: chartData.xAxis,
                        scaleType: 'time',
                      },
                    ]}
                  />
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Box>
    </LocalizationProvider>
  )
}
